import React from "react";
import { Link } from "react-router-dom";
import ArrowOutward from "@mui/icons-material/ArrowOutward";

import {
  SiteShell,
  BrandLink,
  SectionHead,
  NextChapter,
  ProductPaths,
  DEMO_URL,
} from "../components/BrandPrimitives";
import WorkspacePreview from "../components/WorkspacePreview";
import { PAGE_META } from "../utils/shareMeta";
import "../styles/brand-editorial.css";

export function BrandVisionPage() {
  return (
    <SiteShell
      {...PAGE_META.vision}
      className="brand-vision"
    >
      <section className="brand-editorial-hero brand-container">
        <span className="brand-eyebrow">GrupaAI / Founder&apos;s letter</span>
        <h1>
          Pioneering
          <br />
          <span>the future of work.</span>
        </h1>
        <p className="brand-vision-letter-title">
          Our vision for autonomous living and work.
        </p>
        <div className="brand-editorial-intro">
          <p>
            Our mission is to liberate humanity from labor and elevate humanity.
          </p>
          <span className="brand-caption">
            Samuel Isidore Ekpe
            <br />
            Founder &amp; CEO
          </span>
        </div>
      </section>
      <div className="brand-vision-statement">
        <div className="brand-container">
          <p>
            Digital humanoid workers.
            <br />
            <em>Real business outcomes.</em>
          </p>
          <span className="brand-caption">
            GrupaAI / The operating system for autonomous work
          </span>
        </div>
      </div>
      <div className="brand-reading-layout brand-container">
        <aside>
          <span className="brand-eyebrow">The conviction</span>
          <p>
            Written by
            <br />
            <strong>Samuel Isidore Ekpe</strong>
            <br />
            Founder &amp; CEO
          </p>
          <a href="#vision-path">
            How we get there <ArrowOutward fontSize="small" />
          </a>
          <Link to="/about#our-story">
            Our story and team <ArrowOutward fontSize="small" />
          </Link>
        </aside>
        <article className="brand-prose">
          <p className="brand-prose-lead">
            A person&apos;s greatest power is their imagination, their
            relationships, and their voice.
          </p>
          <p>
            Growing up, I hated chores. They took energy from the things I
            wanted to do: imagine, create, and connect with people. That feeling
            never left me. I believe we should spend more of our lives doing
            work that calls on what makes us human.
          </p>
          <p>
            That is why we are building GrupaAI. We are a Pioneer AI Lab turning
            advances in intelligence into useful work. We call the space between
            frontier capability and useful, affordable work the Pioneer Lab Gap.
            We invent across kernels, models, agents, and product to close it.
          </p>
          <h2>A life beyond logistics.</h2>
          <p>
            This vision reaches beyond business. Personal life, family life, and
            professional growth deserve the same freedom from routine work.
            I imagine one life operating system: workers that understand the
            context across our devices, carry plans through to completion, and
            leave us more time for the people and ambitions that matter.
          </p>
          <p>
            It is always about growth: personal growth, family growth and business
            growth. I want workers that know what I am trying to achieve,
            understand what is standing in the way, and work with me to move
            forward.
          </p>
          <p>
            The test is trust. Can I hand off a task and go to sleep, confident
            that it will be handled? That is the experience I want us to earn:
            a worker that is present when needed, ambient when it is not, and
            dependable enough that a person can stop supervising. This is our
            direction for the future, with people choosing what workers can
            access and do on their behalf.
          </p>
          <p>
            I explore that future in my essay,{" "}
            <Link to="/blog/the-future-of-agents-i-see">
              The Future I See
            </Link>.
          </p>
          <h2>The freedom to do nothing.</h2>
          <p>
            I want AI to do the work, including the work of figuring out how to
            get it done. More time for strategy may be an early benefit, but
            strategy should not become the job we are all required to keep.
          </p>
          <p>
            We expect routine operations to be an early foothold, followed by
            more complete execution across teams and systems. As workers become
            more capable, our ambition includes helping develop strategy and
            carrying it through. These capabilities will grow together; this is
            a direction we are building toward, not a fixed timetable.
          </p>
          <p>
            People should be free to set a vision, explore it with their workers,
            or delegate the planning and get on with life. Choosing what matters
            and retaining the ability to change direction should not require a
            day spent managing AI.
          </p>
          <p>
            Elevating humanity means more freedom over our lives: to imagine,
            create, connect, grow, rest, or do nothing. What we do with that
            freedom is ours to choose.
          </p>
          <h2>Digital robotics. A new kind of worker.</h2>
          <p>
            Physical robotics gave machines the ability to work in the world of
            atoms. Digital robotics gives workers the ability to operate in the
            world of software. An autonomous digital humanoid worker should carry
            out a mission across computers, phones, tablets, and watches,
            using apps and browsers as a person would. Our direction is
            concurrent work across local and remote
            devices, with context that moves with the mission.
          </p>
          <h2>The operating system for autonomous business.</h2>
          <p>
            A founder should be able to name a destination and begin moving
            toward it. A team should be able to coordinate complex work without
            spending its best hours coordinating software.
          </p>
          <p>
            We are building the infrastructure and operating system for
            autonomous businesses: workers that operate across devices and systems,
            carry context across tasks, and work together. You choose what matters.
            Workers can help shape the plan and carry out the execution. You can see what happened,
            step in when needed, and change direction without having to supervise
            every step.
          </p>
          <p>
            I want an independent colleague that actually takes work off my
            plate, works directly with other human and AI colleagues, and keeps
            going when I close my laptop.
          </p>
          <p>
            Our infrastructure ambition is <strong>trillions of autonomous digital workers</strong>,
            provisioned for organizations as readily as cloud compute. Making
            that work reliably, securely, and around the clock requires research,
            engineering, and security to advance together. That is the scale we
            are building toward.
          </p>
          <p>
            We start where progress can be measured: finding customers,
            researching markets, moving opportunities forward, and clearing
            operational work. The outcome matters more than the performance of
            intelligence.
          </p>
          <h2>From one worker to an autonomous organization.</h2>
          <p>
            Our path starts with an autonomous digital worker, grows into
            autonomous teams, and ultimately reaches autonomous organizations.
            Workers coordinate routine work across roles and devices, bringing
            people in when their judgment is needed. People set the mission and
            boundaries, and can intervene at any time.
          </p>
          <p>
            Reliable autonomy takes more than a capable model. It takes an
            environment: persistent sessions, appropriate access, coordination,
            evaluation, and a clear moment for a human to decide.
          </p>
          <p>
            Our ambition is to make that foundation accessible enough for a
            single founder to build a remarkable company. We see a future in
            which people direct organizations that are far more capable than
            their headcount suggests.
          </p>
          <blockquote>
            The future we want gives people more agency over their time, their
            work, and their lives.
          </blockquote>
          <h2>Building toward Collective Superintelligence.</h2>
          <p>
            As autonomous organizations learn to work with one another, new
            possibilities open up. Knowledge can move between organizations.
            Workers can coordinate across boundaries. Collective intelligence
            can grow beyond what any one company could create alone.
          </p>
          <p>
            Organizations should choose what their workers share. Learning and
            coordination across organizations must respect permissions,
            confidentiality, and the boundaries of private company data.
          </p>
          <p>
            Each autonomous organization becomes a form of{" "}
            <strong>Swarm Intelligence</strong>. Connected together, they
            become a <strong>Swarm of Swarms</strong>. At global scale, they
            form a <strong>Network of Swarms</strong>.
          </p>
          <p>
            We believe that network will usher in{" "}
            <strong>Collective Superintelligence</strong>: intelligence no
            individual, worker, or organization could create alone.
          </p>
          <p>
            We call this connected future the Agentic Internet. We begin with a
            useful worker, a clear mission, and the discipline to make the next
            step real.
          </p>
          <p>Growing up, I wanted more time to imagine, create and connect with people. That feeling never left me.</p>
          <p>Today, I want that freedom for all of us. More time with our families. More room to grow. More of our lives spent doing the things we keep putting off because there is always work to finish.</p>
          <p>That is why we are building workers you can trust to keep going when you close your laptop.</p>
          <p>Our mission is to liberate humanity from labor and elevate humanity.</p>
          <p><strong>So when the work is done, there is still life left to live.</strong></p>
          <p>If you see that future too, come build it with us.</p>
          <div className="brand-author-signature">
            <span>Samuel Isidore Ekpe</span>
            <p>Founder &amp; CEO, GrupaAI</p>
          </div>
        </article>
      </div>
      <section className="brand-platform-band" id="vision-path">
        <div className="brand-container brand-section">
          <SectionHead
            eyebrow="The path ahead"
            title={
              <>
                One worker.
                <br />A world of possibility.
              </>
            }
          >
            <span className="brand-vision-stage">Autonomous Digital Worker</span>{" "}
            <span className="brand-vision-arrow" aria-hidden="true">&rarr;</span>{" "}
            <span className="brand-vision-stage">Autonomous Teams</span>{" "}
            <span className="brand-vision-arrow" aria-hidden="true">&rarr;</span>{" "}
            <span className="brand-vision-stage">Autonomous Organizations</span>
          </SectionHead>
          <ProductPaths />
          <p className="brand-roadmap-note">
            Our long-term ambition: a one-person unicorn, a ten-person decacorn,
            and a hundred-person trillion-dollar organization. These are
            aspirations that guide our work, not achieved results.
          </p>
        </div>
      </section>
      <NextChapter
        title="A future worth building."
        text="If this is a future you want to help create, let’s talk."
        to="mailto:team@grupa.ai"
        label="Talk to us"
        secondaryLink={{ to: "/manifesto", label: "Read our manifesto" }}
      />
    </SiteShell>
  );
}

const principles = [
  [
    "01",
    "People set the destination.",
    "People choose the mission, the boundaries and what their workers can access. Workers should understand the goal and when to ask. People can change direction at any time. They should also be able to delegate planning and strategy without managing every decision."
  ],
  [
    "02",
    "The outcome is the work.",
    "A worker should carry the job through to a result someone can inspect and use. Build for the last mile: the systems people use, the follow-through, the verified result."
  ],
  [
    "03",
    "Earn the trust to work independently.",
    "People should be able to see what happened without having to watch it happen. Show the actions taken, the progress made, and what remains. Workers should ask when judgment is needed and carry on with the responsibility they have been given."
  ],
  [
    "04",
    "Know the person behind the mission.",
    "A task belongs to a life or a business. Workers should understand the context, constraints and stakes, carrying that understanding across devices, tasks and time. Missions can span days and teams. Workers should recover, coordinate, and keep the goal in view."
  ],
  [
    "05",
    "Work toward our growth.",
    "Personal growth, family growth and business growth. Workers should help people pursue their ambitions and remove the roadblocks in their way."
  ],
  [
    "06",
    "Make dependable workers accessible.",
    "This future should be within reach of individuals, families, small teams and businesses. Affordability, reliability and care belong in the engineering from the beginning. Earn trust through accurate language, considered defaults, useful feedback, and details that work as well as they look."
  ]
];

export function BrandManifestoPage() {
  return (
    <SiteShell {...PAGE_META.manifesto}>
      <section className="brand-editorial-hero brand-container brand-manifesto-hero">
        <span className="brand-eyebrow">The Lab / Manifesto</span>
        <h1>
          Liberate humanity from labor.
          <br />
          <span>Elevate humanity.</span>
        </h1>
        <div className="brand-editorial-intro">
          <p>
            Our vision for autonomous living and work.
            <br />The commitments that guide what we build.
          </p>
          <span className="brand-caption">
            Digital robotics.
            <br />
            Autonomous organizations.
          </span>
        </div>
      </section>
      <section className="brand-reading-layout brand-container">
        <aside>
          <span className="brand-eyebrow">What we believe</span>
          <p>Our vision for autonomous living and work.</p>
          <Link to="/blog/the-future-of-agents-i-see">
            The Future I See <ArrowOutward fontSize="small" />
          </Link>
        </aside>
        <div className="brand-prose">
          <p className="brand-prose-lead">
            We should be able to hand off work and go to sleep.
          </p>
          <p>
            Today, too often, we watch the screen, check every step, and
            eventually take over. We are still doing the work of making sure
            the work gets done.
          </p>
          <p>
            We want workers we can trust with our lives, our families, our work
            and our businesses. Workers that understand what matters, know what
            is at stake, and carry the job through to completion.
          </p>
          <p>
            Our ambition is for AI to take on operations, execution, and strategy.
            People should be able to set the direction, delegate the planning,
            and get on with living, without becoming full-time managers of AI.
          </p>
          <p>
            Elevating humanity means the freedom to choose what to do with our
            lives. That may be imagining a new future, creating something,
            spending time with people we love, resting, or doing nothing.
            Strategy and vision are possibilities, not obligations.
          </p>
          <p>That is the future we are building.</p>
        </div>
      </section>
      <div className="brand-principles brand-container">
        {principles.map(([number, title, text]) => (
          <section key={number}>
            <span>{number}</span>
            <h2>{title}</h2>
            <p>{text}</p>
          </section>
        ))}
      </div>
      <section className="brand-manifesto-opening brand-container">
        <p>
          The work is not done
          <br />
          <em>until the outcome is.</em>
        </p>
        <div>
          <p>
            In 2024, our founder was running a talent marketplace. The work
            meant a relentless loop: find customers, find talent, match them,
            and clear the operational queues. The opportunity was clear. The
            day was never long enough.
          </p>
          <p>
            Samuel founded GrupaAI in March 2026 with a question: what would
            become possible if reliable digital workers could carry that loop
            forward?
          </p>
        </div>
      </section>
      <section className="brand-manifesto-argument brand-container">
        <span className="brand-eyebrow">The missing operating system</span>
        <h2>
          Intelligence needs
          <br />
          an environment to work.
        </h2>
        <div className="brand-editorial-columns">
          <div>
            <h3>From capability to execution.</h3>
            <p>
              Models can reason. Businesses need the work done. The Pioneer Lab
              Gap is the infrastructure between those two realities: runtime,
              device sessions, credentials, guardrails, and coordination.
            </p>
          </div>
          <div>
            <h3>The workplace spans devices.</h3>
            <p>
              CRM, email, recruiting, customer portals, and operations already
              live across devices and systems. Digital humanoid workers need
              to act across them concurrently, locally or remotely, with
              identity, shared context, and a trace of what happened.
            </p>
          </div>
          <div>
            <h3>Economics that reach everyone.</h3>
            <p>
              Expert digital workers should be accessible at meaningful scale.
              We are engineering toward a fully loaded cost below 0.3 cents per
              worker-hour, a research target that shapes work across the stack.
              This is not a customer price or an achieved benchmark.
            </p>
            <Link className="brand-text-link brand-manifesto-economics-link" to="/blog/the-pioneer-lab-gap">
              How we evaluate worker economics <ArrowOutward fontSize="small" />
            </Link>
          </div>
        </div>
      </section>
      <section className="brand-manifesto-argument brand-container">
        <span className="brand-eyebrow">The horizon / Agentic Internet</span>
        <h2>
          Autonomous organizations.
          <br />
          Working for us. Working together.
        </h2>
        <div className="brand-prose brand-horizon-copy">
          <p>
            We want workers that can work directly with other human and AI
            colleagues without bringing every step back to us. They should
            share the context they need, coordinate the work, and carry their
            responsibilities through to completion.
          </p>
          <p>
            That is how we build toward an autonomous organization. People set
            the mission and direction. Workers carry the operations forward,
            even when we close our laptops.
          </p>
          <p>
            Our long-term vision extends beyond a single company. Our workers
            should be able to work with your workers, so organizations can
            coordinate, negotiate and collaborate through their agents.
          </p>
          <p>
            We call that connected future the Agentic Internet. From that
            network, we see the possibility of{" "}
            <strong>Collective Superintelligence:</strong>{" "}
            intelligence no one organization could create alone.
          </p>
        </div>
      </section>
      <section className="brand-manifesto-close brand-container">
        <span className="brand-eyebrow">Our commitment</span>
        <h2>
          Begin with a useful worker.
          <br />
          Keep building toward
          <br />
          <em>something extraordinary.</em>
        </h2>
        <div className="brand-prose">
          <p>
            We are building toward autonomous workers, autonomous teams and
            autonomous organizations.
          </p>
          <p>
            Every step should give people more freedom to live, create, grow
            and spend time with the people they love.
          </p>
          <p>
            If you build, bring us the problem you cannot stop thinking about.
            If you run a business, show us the work you still cannot hand off.
            Help us build workers you would trust to finish it.
          </p>
          <p>
            Our mission is to liberate humanity from labor and elevate humanity.
          </p>
          <p>If you see what we see, come build it with us.</p>
        </div>
        <div className="brand-actions">
          <BrandLink to="/desktop#downloads">Meet Momentum</BrandLink>
          <Link className="brand-text-link" to="/vision">
            Read our vision <ArrowOutward fontSize="small" />
          </Link>
        </div>
      </section>
      <NextChapter
        title="A place for pioneers."
        text="Bring a problem you cannot stop thinking about."
        to="/careers"
        label="Build with us"
      />
    </SiteShell>
  );
}

export function BrandWorkspacePage() {
  return (
    <SiteShell
      {...PAGE_META.workspace}
      className="brand-workspace"
    >
      <section className="brand-editorial-hero brand-container">
        <span className="brand-eyebrow">Product / Workspace preview</span>
        <h1>
          Workspace.
          <br />
          <span>Your agents. Together.</span>
        </h1>
        <div className="brand-editorial-intro">
          <p>
            Your agents, working together. Your company, moving forward.
            <br />
            Digital humanoids coordinate the work on your behalf, and bring you
            in only when they need you.
          </p>
          <div className="brand-actions">
            <BrandLink to="/workspace#workspace-preview">Explore Workspace</BrandLink>
            <a
              href={DEMO_URL}
              className="brand-text-link"
              target="_blank"
              rel="noreferrer"
            >
              Talk to our team <ArrowOutward fontSize="small" />
            </a>
          </div>
        </div>
      </section>
      <section id="workspace-preview" className="brand-container brand-workspace-preview">
        <WorkspacePreview />
      </section>
      <section className="brand-section brand-container">
        <SectionHead
          eyebrow="Work moves without waiting"
          title={
            <>
              Agents at work.
              <br />
              Humans when needed.
            </>
          }
        >
          Every agent represents a human. Together, they share context, resolve
          dependencies, and carry the mission forward. You set the direction and
          boundaries. They bring you a decision when your judgment, permission,
          or missing context is genuinely needed.
        </SectionHead>
        <div className="brand-editorial-columns">
          {[
            [
              "A team on your behalf",
              "Named digital humanoids with clear roles, persistent context, and responsibility for the outcome. Your agents work with other agents across the company.",
            ],
            [
              "Coordination without the wait",
              "Agents exchange context, coordinate work across devices in parallel, and verify handoffs. Routine collaboration does not need to pass through a human.",
            ],
            [
              "Only the decisions you need",
              "When a boundary is reached, the right human receives the context, the options, and a clear request. The rest of the team keeps working.",
            ],
          ].map(([t, p], i) => (
            <div key={t}>
              <span className="brand-caption">0{i + 1}</span>
              <h3>{t}</h3>
              <p>{p}</p>
            </div>
          ))}
        </div>
      </section>
      <NextChapter
        title="Give your agents a place to work."
        text="A company moving forward. A team working on your behalf."
        to={DEMO_URL}
        label="Talk to our team"
      />
    </SiteShell>
  );
}
